import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import ImageUploader from '@/components/ImageUploader';
import MultiImageUploader from '@/components/MultiImageUploader';
import Logo from '@/components/Logo';
import { carsAPI, appointmentsAPI, contactsAPI, authAPI, getAuthToken, usersAPI, estimationsAPI, emailContactsAPI, testimonialsAPI } from '@/services/api';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Car,
  Calendar,
  Mail,
  LogOut,
  Plus,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Users,
  Key,
  Sparkles,
  Languages,
  ClipboardList,
  Image as ImageIcon,
  Download,
  AtSign,
  ShieldCheck,
  Star,
  MessageSquare
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [estimations, setEstimations] = useState([]);
  const [emailContacts, setEmailContacts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [emailSourceFilter, setEmailSourceFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cars');

  // User management state
  const [newUser, setNewUser] = useState({ username: '', password: '', email: '' });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });

  // Car form state
  const [isCarDialogOpen, setIsCarDialogOpen] = useState(false);
  const [editingCar, setEditingCar] = useState(null);
  const [carForm, setCarForm] = useState({
    make: '',
    model: '',
    year: '',
    price: '',
    mileage: '',
    fuel: 'Petrol',
    transmission: 'Manual',
    color: '',
    description: '',
    features: '',
    image: '',
    images: '', // Additional images (comma-separated URLs)
    status: 'available',
    warranty_included: true
  });

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [carsRes, appointmentsRes, contactsRes, usersRes, estimationsRes, emailsRes, testimonialsRes] = await Promise.all([
        carsAPI.getAll(),
        appointmentsAPI.getAll(),
        contactsAPI.getAll(),
        usersAPI.getAll(),
        estimationsAPI.getAll(),
        emailContactsAPI.getAll(),
        testimonialsAPI.adminList()
      ]);
      setCars(carsRes.data);
      setAppointments(appointmentsRes.data);
      setContacts(contactsRes.data);
      setUsers(usersRes.data);
      setEstimations(estimationsRes.data);
      setEmailContacts(emailsRes.data);
      setTestimonials(testimonialsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/admin/login');
      } else {
        toast.error('Error loading data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  // Car Management Functions
  const openCarDialog = (car = null) => {
    if (car) {
      setEditingCar(car);
      setCarForm({
        ...car,
        features: car.features.join(', '),
        images: (car.images || []).join(', '), // Convert array to comma-separated string
        year: car.year.toString(),
        price: car.price.toString(),
        mileage: car.mileage.toString(),
        warranty_included: car.warranty_included !== false
      });
    } else {
      setEditingCar(null);
      setCarForm({
        make: '',
        model: '',
        year: '',
        price: '',
        mileage: '',
        fuel: 'Petrol',
        transmission: 'Manual',
        color: '',
        description: '',
        features: '',
        image: '',
        images: '',
        status: 'available',
        warranty_included: true
      });
    }
    setIsCarDialogOpen(true);
  };

  const handleCarSubmit = async (e) => {
    e.preventDefault();
    try {
      const carData = {
        ...carForm,
        year: parseInt(carForm.year),
        price: parseFloat(carForm.price),
        mileage: parseInt(carForm.mileage),
        features: carForm.features.split(',').map(f => f.trim()).filter(f => f),
        images: carForm.images ? carForm.images.split(',').map(url => url.trim()).filter(url => url) : []
      };

      if (editingCar) {
        await carsAPI.update(editingCar.id, carData);
        toast.success('Car updated successfully');
      } else {
        await carsAPI.create(carData);
        toast.success('Car added successfully');
      }

      setIsCarDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving car:', error);
      toast.error(error.response?.data?.detail || 'Error saving car');
    }
  };

  const handleDeleteCar = async (carId) => {
    if (!window.confirm('Are you sure you want to delete this car?')) return;

    try {
      await carsAPI.delete(carId);
      toast.success('Car deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting car:', error);
      toast.error('Error deleting car');
    }
  };

  // Appointment Management
  const handleUpdateAppointment = async (appointmentId, status) => {
    try {
      await appointmentsAPI.update(appointmentId, { status });
      toast.success('Appointment updated');
      fetchData();
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error('Error updating appointment');
    }
  };

  // Contact Management
  const handleUpdateContact = async (contactId, status) => {
    try {
      await contactsAPI.update(contactId, { status });
      toast.success('Contact updated');
      fetchData();
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Error updating contact');
    }
  };

  // Estimation request decision
  const handleEstimationDecision = async (id, decision) => {
    if (!window.confirm(decision === 'accepted' ? 'Accepter ce dossier ? Le client recevra un email.' : 'Refuser ce dossier ? Le client recevra un email.')) return;
    try {
      await estimationsAPI.update(id, { decision });
      toast.success(decision === 'accepted' ? 'Dossier accepté' : 'Dossier refusé');
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la décision");
    }
  };

  // Testimonial moderation
  const handleTestimonialStatus = async (id, status) => {
    const label = status === 'approved' ? 'Approuver cet avis ?' : status === 'rejected' ? 'Refuser cet avis ?' : 'Repasser cet avis en attente ?';
    if (!window.confirm(label)) return;
    try {
      await testimonialsAPI.adminUpdate(id, { status });
      toast.success(status === 'approved' ? 'Avis approuvé' : status === 'rejected' ? 'Avis refusé' : 'Avis remis en attente');
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleTestimonialDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement cet avis ?')) return;
    try {
      await testimonialsAPI.adminDelete(id);
      toast.success('Avis supprimé');
      fetchData();
    } catch (err) {
      console.error(err);
      toast.error('Erreur lors de la suppression');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-blue-900 text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 py-4">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Logo size="md" />
            <div className="border-l border-slate-200 pl-3 ml-1">
              <h1 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin Dashboard</h1>
              <p className="text-xs text-slate-500">Manage your garage</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-blue-900 hover:text-blue-700 text-sm font-semibold">
              View Website
            </a>
            <Button onClick={handleLogout} className="btn-secondary">
              <LogOut size={18} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Total Cars</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{cars.length}</p>
              </div>
              <Car className="text-blue-900" size={40} />
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Pending Appointments</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {appointments.filter(a => a.status === 'pending').length}
                </p>
              </div>
              <Calendar className="text-blue-900" size={40} />
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">New Messages</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">
                  {contacts.filter(c => c.status === 'new').length}
                </p>
              </div>
              <Mail className="text-blue-900" size={40} />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border-slate-200 flex flex-wrap h-auto">
            <TabsTrigger value="cars" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              Cars
            </TabsTrigger>
            <TabsTrigger value="appointments" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              Appointments
            </TabsTrigger>
            <TabsTrigger value="estimations" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white" data-testid="tab-estimations">
              Estimations
              {estimations.filter(e => e.status === 'pending').length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-blue-900 text-white text-[10px] font-bold px-1.5">
                  {estimations.filter(e => e.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="contacts" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              Messages
            </TabsTrigger>
            <TabsTrigger value="emails" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white" data-testid="tab-emails">
              Emails
            </TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white" data-testid="tab-reviews">
              Avis
              {testimonials.filter(x => x.status === 'pending').length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-amber-500 text-white text-[10px] font-bold px-1.5">
                  {testimonials.filter(x => x.status === 'pending').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              Users
            </TabsTrigger>
          </TabsList>

          {/* Cars Tab */}
          <TabsContent value="cars" className="mt-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Manage Cars</h2>
              <Button onClick={() => openCarDialog()} className="btn-primary">
                <Plus size={18} className="mr-2" />
                Add New Car
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cars.map((car) => (
                <div key={car.id} className={`bg-white border rounded-lg overflow-hidden relative ${car.is_latest_arrival ? 'border-blue-900 ring-1 ring-blue-900/40' : 'border-slate-200'}`}>
                  <div className="relative">
                    <img src={car.image} alt={`${car.make} ${car.model}`} className="w-full h-48 object-cover" />
                    {car.is_latest_arrival && (
                      <div className="absolute top-2 left-2 bg-blue-900 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-1 flex items-center gap-1" data-testid={`latest-badge-${car.id}`}>
                        <Sparkles size={10} />
                        Dernière rentrée
                      </div>
                    )}
                    <button
                      onClick={async () => {
                        try {
                          const newVal = !car.is_latest_arrival;
                          await carsAPI.update(car.id, { is_latest_arrival: newVal });
                          toast.success(newVal ? 'Marquée comme dernière rentrée' : 'Retiré de la dernière rentrée');
                          fetchData();
                        } catch (err) {
                          toast.error('Erreur');
                        }
                      }}
                      title={car.is_latest_arrival ? 'Retirer de "Dernière rentrée"' : 'Marquer comme "Dernière rentrée"'}
                      className={`absolute top-2 right-2 w-9 h-9 flex items-center justify-center transition-all ${car.is_latest_arrival ? 'bg-blue-900 text-white' : 'bg-white/60 hover:bg-blue-900/80 text-blue-900 hover:text-white'}`}
                      data-testid={`latest-toggle-${car.id}`}
                    >
                      <Sparkles size={16} />
                    </button>
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-lg font-bold text-slate-900">{car.make} {car.model}</h3>
                      <Badge className={
                        car.status === 'available' ? 'bg-green-500' :
                        car.status === 'reserved' ? 'bg-yellow-500 text-white' :
                        car.status === 'sold' ? 'bg-gray-500' :
                        'bg-red-500'
                      }>
                        {car.status}
                      </Badge>
                    </div>
                    <p className="text-slate-500 text-sm mb-2">{car.year} • {car.mileage.toLocaleString()} km</p>
                    <p className="text-blue-900 text-xl font-bold mb-3">€{car.price.toLocaleString()}</p>
                    {/* Warranty toggle (quick) */}
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const newVal = !(car.warranty_included !== false);
                          await carsAPI.update(car.id, { warranty_included: newVal });
                          toast.success(newVal ? 'Garantie activée' : 'Garantie désactivée');
                          fetchData();
                        } catch (err) { toast.error('Erreur'); }
                      }}
                      className={`mb-3 w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                        car.warranty_included !== false
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                          : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
                      }`}
                      data-testid={`warranty-toggle-${car.id}`}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <ShieldCheck size={13} />
                        Garantie 1 an
                      </span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${car.warranty_included !== false ? 'text-emerald-700' : 'text-slate-400'}`}>
                        {car.warranty_included !== false ? 'Activée' : 'Désactivée'}
                      </span>
                    </button>
                    {/* Quick Status Change */}
                    <div className="grid grid-cols-2 gap-1 mb-3">
                      {['available', 'reserved', 'sold', 'coming_soon'].map((s) => (
                        <button
                          key={s}
                          onClick={async () => {
                            try {
                              await carsAPI.update(car.id, { status: s });
                              toast.success(`Status: ${s.replace('_', ' ')}`);
                              fetchData();
                            } catch (err) { toast.error('Error updating status'); }
                          }}
                          className={`text-[10px] uppercase tracking-wider py-1.5 border transition-colors ${car.status === s ? 'bg-blue-900 text-white border-blue-900 font-bold' : 'bg-transparent text-slate-500 border-slate-200 hover:border-blue-900/40'}`}
                          data-testid={`car-status-${s}-${car.id}`}
                        >
                          {s === 'coming_soon' ? 'Coming soon' : s}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2 mb-2">
                      <Button onClick={() => openCarDialog(car)} className="flex-1 btn-secondary text-sm">
                        <Edit size={14} className="mr-1" />
                        Edit
                      </Button>
                      <Button onClick={() => handleDeleteCar(car.id)} className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm">
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                    {/* Translation status + re-translate */}
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          toast.info('Traduction en cours…');
                          const token = localStorage.getItem('admin_token');
                          await axios.post(
                            `${process.env.REACT_APP_BACKEND_URL}/api/admin/cars/${car.id}/translate`,
                            {},
                            { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
                          );
                          toast.success('Traduction NL/EN mise à jour');
                          fetchData();
                        } catch (err) {
                          toast.error('Échec de la traduction');
                        }
                      }}
                      className="w-full text-[10px] uppercase tracking-wider py-1.5 border border-slate-200 text-blue-900 hover:bg-blue-900/10 transition-colors flex items-center justify-center gap-1.5"
                      data-testid={`retranslate-${car.id}`}
                      title="Re-traduire description et options en NL et EN"
                    >
                      <Languages size={11} />
                      {(car.description_translations?.en && car.description_translations?.nl) ? 'Re-traduire NL/EN' : 'Traduire NL/EN'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="appointments" className="mt-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Appointment Requests</h2>
            <div className="space-y-4">
              {appointments.map((appointment) => (
                <div key={appointment.id} className="bg-white border border-slate-200 rounded-lg p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-lg font-bold text-slate-900">{appointment.name}</h3>
                        <Badge className={
                          appointment.status === 'confirmed' ? 'bg-green-500' :
                          appointment.status === 'pending' ? 'bg-yellow-500' :
                          appointment.status === 'completed' ? 'bg-blue-500' :
                          'bg-red-500'
                        }>
                          {appointment.status}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Email: <span className="text-slate-900">{appointment.email}</span></p>
                          <p className="text-slate-500">Phone: <span className="text-slate-900">{appointment.phone}</span></p>
                        </div>
                        <div>
                          <p className="text-slate-500">Date: <span className="text-slate-900">{appointment.date}</span></p>
                          <p className="text-slate-500">Time: <span className="text-slate-900">{appointment.time}</span></p>
                        </div>
                      </div>
                      {appointment.message && (
                        <p className="text-slate-700 mt-3 text-sm italic">"{appointment.message}"</p>
                      )}
                    </div>
                    <div className="flex gap-2 ml-4">
                      {appointment.status === 'pending' && (
                        <Button 
                          onClick={() => handleUpdateAppointment(appointment.id, 'confirmed')}
                          className="bg-green-600 hover:bg-green-700 text-slate-900"
                          size="sm"
                        >
                          <CheckCircle size={16} className="mr-1" />
                          Confirm
                        </Button>
                      )}
                      {appointment.status === 'confirmed' && (
                        <Button 
                          onClick={() => handleUpdateAppointment(appointment.id, 'completed')}
                          className="bg-blue-600 hover:bg-blue-700 text-slate-900"
                          size="sm"
                        >
                          <CheckCircle size={16} className="mr-1" />
                          Complete
                        </Button>
                      )}
                      {(appointment.status === 'pending' || appointment.status === 'confirmed') && (
                        <Button 
                          onClick={() => handleUpdateAppointment(appointment.id, 'cancelled')}
                          className="bg-red-600 hover:bg-red-700 text-slate-900"
                          size="sm"
                        >
                          <XCircle size={16} className="mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {appointments.length === 0 && (
                <p className="text-slate-500 text-center py-8">No appointments yet</p>
              )}
            </div>
          </TabsContent>

          {/* Estimations Tab */}
          <TabsContent value="estimations" className="mt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <ClipboardList className="text-blue-900" size={22} />
                Demandes d'estimation
              </h2>
              <p className="text-slate-500 text-sm">{estimations.length} demande(s)</p>
            </div>
            <div className="space-y-4">
              {estimations.map((req) => {
                const fullName = `${req.first_name || ''} ${req.last_name || ''}`.trim();
                const carLabel = `${req.make || ''} ${req.model || ''}${req.year ? ` (${req.year})` : ''}`.trim();
                const created = req.created_at ? new Date(req.created_at).toLocaleString('fr-BE') : '';
                const transmissionLabel = ({ manual: 'Manuelle', automatic: 'Automatique', 'semi-automatic': 'Semi-auto' })[req.transmission] || req.transmission;
                return (
                  <div
                    key={req.id}
                    className="bg-white border border-slate-200 rounded-lg p-6"
                    data-testid={`estimation-row-${req.id}`}
                  >
                    <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="text-lg font-bold text-slate-900">{fullName || 'Sans nom'}</h3>
                          <Badge className={
                            req.status === 'accepted' ? 'bg-green-500' :
                            req.status === 'rejected' ? 'bg-red-500' :
                            'bg-yellow-500 text-white'
                          }>
                            {req.status === 'accepted' ? 'Accepté' : req.status === 'rejected' ? 'Refusé' : 'En attente'}
                          </Badge>
                        </div>
                        <p className="text-slate-500 text-xs">{created}</p>
                      </div>
                      {req.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleEstimationDecision(req.id, 'accepted')}
                            className="bg-green-600 hover:bg-green-700 text-slate-900"
                            size="sm"
                            data-testid={`estimation-accept-${req.id}`}
                          >
                            <CheckCircle size={16} className="mr-1" />
                            Accepter
                          </Button>
                          <Button
                            onClick={() => handleEstimationDecision(req.id, 'rejected')}
                            className="bg-red-600 hover:bg-red-700 text-slate-900"
                            size="sm"
                            data-testid={`estimation-reject-${req.id}`}
                          >
                            <XCircle size={16} className="mr-1" />
                            Refuser
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                      <div className="space-y-1.5">
                        <p className="text-slate-500">Email : <a href={`mailto:${req.email}`} className="text-blue-900 hover:underline">{req.email}</a></p>
                        {req.phone && (
                          <p className="text-slate-500">Téléphone : <a href={`tel:${req.phone}`} className="text-slate-900">{req.phone}</a></p>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <p className="text-slate-500">Véhicule : <span className="text-slate-900">{carLabel}</span></p>
                        <p className="text-slate-500">
                          {req.fuel} · {transmissionLabel} · {Number(req.mileage || 0).toLocaleString('fr-BE')} km
                        </p>
                        {req.desired_price ? (
                          <p className="text-slate-500">Prix souhaité : <span className="text-blue-900 font-semibold">€{Number(req.desired_price).toLocaleString('fr-BE')}</span></p>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${req.ctech_valid ? 'bg-green-600/20 text-green-300 border border-green-600/30' : 'bg-slate-200/40 text-slate-500 border border-gray-600/30'}`}>
                        CT {req.ctech_valid ? 'valide' : 'non valide'}
                      </span>
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${req.carpass ? 'bg-green-600/20 text-green-300 border border-green-600/30' : 'bg-slate-200/40 text-slate-500 border border-gray-600/30'}`}>
                        Car-Pass {req.carpass ? 'oui' : 'non'}
                      </span>
                    </div>

                    {req.extra_info && (
                      <div className="bg-white/40 border border-blue-900/10 p-3 mb-4">
                        <p className="text-slate-500 text-[10px] tracking-widest uppercase mb-1">Informations complémentaires</p>
                        <p className="text-gray-200 text-sm whitespace-pre-wrap">{req.extra_info}</p>
                      </div>
                    )}

                    {(req.photos && req.photos.length > 0) && (
                      <div>
                        <p className="text-slate-500 text-[10px] tracking-widest uppercase mb-2 flex items-center gap-1.5">
                          <ImageIcon size={11} /> Photos ({req.photos.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {req.photos.map((url, i) => (
                            <a
                              key={i}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block border border-slate-200 hover:border-blue-900/60 transition-colors"
                              data-testid={`estimation-photo-${req.id}-${i}`}
                            >
                              <img src={url} alt={`Photo ${i + 1}`} className="w-24 h-20 object-cover" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {estimations.length === 0 && (
                <p className="text-slate-500 text-center py-8">Aucune demande pour le moment</p>
              )}
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts" className="mt-6">
            <h2 className="text-2xl font-bold text-slate-900 mb-6">Contact Messages</h2>
            <div className="space-y-4">
              {contacts.map((contact) => (
                <div key={contact.id} className="bg-white border border-slate-200 rounded-lg p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-lg font-bold text-slate-900">{contact.name}</h3>
                        <Badge className={
                          contact.status === 'new' ? 'bg-green-500' :
                          contact.status === 'read' ? 'bg-yellow-500' :
                          'bg-blue-500'
                        }>
                          {contact.status}
                        </Badge>
                      </div>
                      <div className="text-sm mb-3">
                        <p className="text-slate-500">Email: <span className="text-slate-900">{contact.email}</span></p>
                        {contact.phone && (
                          <p className="text-slate-500">Phone: <span className="text-slate-900">{contact.phone}</span></p>
                        )}
                        {contact.subject && (
                          <p className="text-slate-500">Subject: <span className="text-slate-900">{contact.subject}</span></p>
                        )}
                      </div>
                      <p className="text-slate-700 text-sm">{contact.message}</p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      {contact.status === 'new' && (
                        <Button 
                          onClick={() => handleUpdateContact(contact.id, 'read')}
                          className="btn-secondary"
                          size="sm"
                        >
                          <Eye size={16} className="mr-1" />
                          Mark Read
                        </Button>
                      )}
                      {contact.status === 'read' && (
                        <Button 
                          onClick={() => handleUpdateContact(contact.id, 'replied')}
                          className="bg-blue-600 hover:bg-blue-700 text-slate-900"
                          size="sm"
                        >
                          <CheckCircle size={16} className="mr-1" />
                          Replied
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {contacts.length === 0 && (
                <p className="text-slate-500 text-center py-8">No messages yet</p>
              )}
            </div>
          </TabsContent>

          {/* Emails / Newsletter Tab */}
          <TabsContent value="emails" className="mt-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <AtSign className="text-blue-900" size={22} />
                Base e-mails
                <span className="text-sm text-slate-500 font-normal">
                  ({emailContacts.length})
                </span>
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={emailSourceFilter}
                  onChange={(e) => setEmailSourceFilter(e.target.value)}
                  className="bg-white border border-slate-200 text-slate-900 text-sm px-3 py-2"
                  data-testid="emails-source-filter"
                >
                  <option value="">Toutes les sources</option>
                  <option value="newsletter">Newsletter</option>
                  <option value="estimation">Estimations</option>
                  <option value="appointment">Rendez-vous</option>
                  <option value="contact">Contact</option>
                </select>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-contacts.csv${emailSourceFilter ? `?source=${emailSourceFilter}` : ''}`}
                  onClick={async (e) => {
                    e.preventDefault();
                    try {
                      const token = getAuthToken();
                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/email-contacts.csv${emailSourceFilter ? `?source=${emailSourceFilter}` : ''}`;
                      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                      if (!res.ok) throw new Error('Download failed');
                      const blob = await res.blob();
                      const blobUrl = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = blobUrl;
                      a.download = 'autodistrict-email-contacts.csv';
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      URL.revokeObjectURL(blobUrl);
                      toast.success('CSV téléchargé');
                    } catch (err) {
                      toast.error('Erreur lors du téléchargement');
                    }
                  }}
                  className="inline-flex items-center gap-2 bg-blue-900 hover:bg-yellow-400 text-white text-sm font-bold px-4 py-2 transition-colors"
                  data-testid="emails-download-csv"
                >
                  <Download size={16} />
                  Télécharger CSV
                </a>
              </div>
            </div>

            <div className="overflow-x-auto bg-white border border-slate-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-white/40 border-b border-slate-200">
                  <tr>
                    <th className="text-left text-[10px] uppercase tracking-widest text-slate-500 px-4 py-3">Email</th>
                    <th className="text-left text-[10px] uppercase tracking-widest text-slate-500 px-4 py-3">Prénom</th>
                    <th className="text-left text-[10px] uppercase tracking-widest text-slate-500 px-4 py-3">Source</th>
                    <th className="text-left text-[10px] uppercase tracking-widest text-slate-500 px-4 py-3">Langue</th>
                    <th className="text-left text-[10px] uppercase tracking-widest text-slate-500 px-4 py-3">Date</th>
                    <th className="text-right text-[10px] uppercase tracking-widest text-slate-500 px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {emailContacts
                    .filter((c) => !emailSourceFilter || c.source === emailSourceFilter)
                    .map((c) => (
                      <tr key={c.id} className="border-b border-blue-900/5 hover:bg-white/30 transition-colors" data-testid={`email-row-${c.id}`}>
                        <td className="px-4 py-3 text-slate-900">{c.email}</td>
                        <td className="px-4 py-3 text-slate-700">{c.first_name || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${
                            c.source === 'newsletter' ? 'bg-emerald-600/20 text-emerald-300 border border-emerald-600/30'
                            : c.source === 'estimation' ? 'bg-amber-600/20 text-amber-300 border border-amber-600/30'
                            : c.source === 'appointment' ? 'bg-sky-600/20 text-sky-300 border border-sky-600/30'
                            : 'bg-gray-600/20 text-slate-700 border border-gray-600/30'
                          }`}>
                            {c.source}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-500 uppercase text-xs">{c.language || '—'}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs">
                          {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-BE') : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Supprimer ${c.email} de la base ?`)) return;
                              try {
                                await emailContactsAPI.remove(c.id);
                                toast.success('Supprimé');
                                fetchData();
                              } catch (err) {
                                toast.error('Erreur');
                              }
                            }}
                            className="text-red-400 hover:text-red-300 transition-colors"
                            data-testid={`email-delete-${c.id}`}
                            title="Supprimer"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  {emailContacts.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                        Aucun e-mail collecté pour le moment.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews" className="mt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900">
                Avis clients
                <span className="ml-3 text-sm font-normal text-slate-500">
                  {testimonials.length} au total · {testimonials.filter(t => t.status === 'pending').length} en attente
                </span>
              </h2>
            </div>

            {testimonials.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-lg p-12 text-center">
                <MessageSquare className="mx-auto mb-3 text-slate-300" size={40} />
                <p className="text-slate-600">Aucun avis reçu pour le moment.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="admin-testimonials-list">
                {testimonials.map((rev) => {
                  const badgeCls =
                    rev.status === 'approved'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : rev.status === 'rejected'
                        ? 'bg-rose-50 text-rose-700 border-rose-200'
                        : 'bg-amber-50 text-amber-700 border-amber-200';
                  const badgeLabel =
                    rev.status === 'approved' ? 'Approuvé' : rev.status === 'rejected' ? 'Refusé' : 'En attente';
                  return (
                    <div
                      key={rev.id}
                      className="bg-white border border-slate-200 rounded-xl p-5"
                      data-testid={`admin-testimonial-${rev.id}`}
                    >
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <p className="text-slate-900 font-bold">{rev.name}</p>
                            <span className={`text-[10px] uppercase tracking-wider font-semibold border rounded-full px-2 py-0.5 ${badgeCls}`}>
                              {badgeLabel}
                            </span>
                            <div className="flex items-center gap-0.5" aria-label={`${rev.rating}/5`}>
                              {[1, 2, 3, 4, 5].map((n) => (
                                <Star
                                  key={n}
                                  size={13}
                                  className={n <= rev.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-200'}
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-xs text-slate-500">
                            {[rev.city, rev.vehicle].filter(Boolean).join(' · ') || '—'}
                            {rev.email && <> · <span className="text-slate-400">{rev.email}</span></>}
                          </p>
                          <p className="mt-3 text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                            {rev.comment}
                          </p>
                          <p className="mt-3 text-[11px] text-slate-400">
                            Reçu le {new Date(rev.created_at).toLocaleString('fr-BE')}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {rev.status !== 'approved' && (
                            <Button
                              size="sm"
                              onClick={() => handleTestimonialStatus(rev.id, 'approved')}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`admin-testimonial-approve-${rev.id}`}
                            >
                              <CheckCircle size={14} className="mr-1" />
                              Approuver
                            </Button>
                          )}
                          {rev.status !== 'rejected' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleTestimonialStatus(rev.id, 'rejected')}
                              className="border-rose-200 text-rose-700 hover:bg-rose-50"
                              data-testid={`admin-testimonial-reject-${rev.id}`}
                            >
                              <XCircle size={14} className="mr-1" />
                              Refuser
                            </Button>
                          )}
                          {rev.status !== 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleTestimonialStatus(rev.id, 'pending')}
                              className="border-slate-200 text-slate-600 hover:bg-slate-50"
                              data-testid={`admin-testimonial-pending-${rev.id}`}
                            >
                              Remettre en attente
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleTestimonialDelete(rev.id)}
                            className="border-rose-200 text-rose-700 hover:bg-rose-50"
                            data-testid={`admin-testimonial-delete-${rev.id}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Change Password */}
              <div className="bg-white border border-slate-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Key className="text-blue-900" size={20} />
                  <h3 className="text-lg font-bold text-slate-900">Change Password</h3>
                </div>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (passwordForm.new_password !== passwordForm.confirm_password) {
                    toast.error('New passwords do not match');
                    return;
                  }
                  try {
                    await usersAPI.changePassword({
                      current_password: passwordForm.current_password,
                      new_password: passwordForm.new_password
                    });
                    toast.success('Password changed successfully');
                    setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Error changing password');
                  }
                }} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-500 text-xs uppercase tracking-wider">Current Password</Label>
                    <Input
                      type="password" value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                      className="bg-white border-slate-200 text-slate-900" required
                      data-testid="change-password-current"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-500 text-xs uppercase tracking-wider">New Password</Label>
                    <Input
                      type="password" value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                      className="bg-white border-slate-200 text-slate-900" required minLength={6}
                      data-testid="change-password-new"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-500 text-xs uppercase tracking-wider">Confirm New Password</Label>
                    <Input
                      type="password" value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                      className="bg-white border-slate-200 text-slate-900" required minLength={6}
                      data-testid="change-password-confirm"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-primary" data-testid="change-password-btn">
                    Update Password
                  </Button>
                </form>
              </div>

              {/* Add New User */}
              <div className="bg-white border border-slate-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Plus className="text-blue-900" size={20} />
                  <h3 className="text-lg font-bold text-slate-900">Add Admin User</h3>
                </div>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    await usersAPI.create(newUser);
                    toast.success(`User "${newUser.username}" created`);
                    setNewUser({ username: '', password: '', email: '' });
                    fetchData();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Error creating user');
                  }
                }} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-500 text-xs uppercase tracking-wider">Username *</Label>
                    <Input
                      value={newUser.username}
                      onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                      className="bg-white border-slate-200 text-slate-900" required
                      data-testid="add-user-username"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-500 text-xs uppercase tracking-wider">Email</Label>
                    <Input
                      type="email" value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      className="bg-white border-slate-200 text-slate-900"
                      data-testid="add-user-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-500 text-xs uppercase tracking-wider">Password *</Label>
                    <Input
                      type="password" value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      className="bg-white border-slate-200 text-slate-900" required minLength={6}
                      data-testid="add-user-password"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-primary" data-testid="add-user-btn">
                    Create User
                  </Button>
                </form>
              </div>
            </div>

            {/* Existing Users List */}
            <div className="mt-8">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Users className="text-blue-900" size={20} />
                Admin Users ({users.length})
              </h3>
              <div className="space-y-3">
                {users.map((user) => (
                  <div key={user.id} className="bg-white border border-slate-200 rounded-lg p-4 flex justify-between items-center" data-testid={`user-row-${user.id}`}>
                    <div>
                      <p className="text-slate-900 font-semibold">{user.username}</p>
                      <p className="text-slate-500 text-xs">{user.email || 'No email'}</p>
                    </div>
                    <Button
                      onClick={async () => {
                        if (!window.confirm(`Delete user "${user.username}"?`)) return;
                        try {
                          await usersAPI.delete(user.id);
                          toast.success(`User "${user.username}" deleted`);
                          fetchData();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Error deleting user');
                        }
                      }}
                      className="bg-red-600 hover:bg-red-700 text-slate-900 text-sm"
                      data-testid={`delete-user-${user.id}`}
                    >
                      <Trash2 size={14} className="mr-1" /> Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Car Dialog */}
      <Dialog open={isCarDialogOpen} onOpenChange={setIsCarDialogOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-blue-900">
              {editingCar ? 'Edit Car' : 'Add New Car'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCarSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Make *</Label>
                <Input
                  value={carForm.make}
                  onChange={(e) => setCarForm({...carForm, make: e.target.value})}
                  className="bg-white border-slate-200 text-slate-900"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Model *</Label>
                <Input
                  value={carForm.model}
                  onChange={(e) => setCarForm({...carForm, model: e.target.value})}
                  className="bg-white border-slate-200 text-slate-900"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Year *</Label>
                <Input
                  type="number"
                  value={carForm.year}
                  onChange={(e) => setCarForm({...carForm, year: e.target.value})}
                  className="bg-white border-slate-200 text-slate-900"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Price (€) *</Label>
                <Input
                  type="number"
                  value={carForm.price}
                  onChange={(e) => setCarForm({...carForm, price: e.target.value})}
                  className="bg-white border-slate-200 text-slate-900"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Mileage (km) *</Label>
                <Input
                  type="number"
                  value={carForm.mileage}
                  onChange={(e) => setCarForm({...carForm, mileage: e.target.value})}
                  className="bg-white border-slate-200 text-slate-900"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Fuel Type *</Label>
                <Select value={carForm.fuel} onValueChange={(value) => setCarForm({...carForm, fuel: value})}>
                  <SelectTrigger className="bg-white border-slate-200 text-slate-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="Petrol" className="text-slate-900">Petrol</SelectItem>
                    <SelectItem value="Diesel" className="text-slate-900">Diesel</SelectItem>
                    <SelectItem value="Electric" className="text-slate-900">Electric</SelectItem>
                    <SelectItem value="Hybrid" className="text-slate-900">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Transmission *</Label>
                <Select value={carForm.transmission} onValueChange={(value) => setCarForm({...carForm, transmission: value})}>
                  <SelectTrigger className="bg-white border-slate-200 text-slate-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="Manual" className="text-slate-900">Manual</SelectItem>
                    <SelectItem value="Automatic" className="text-slate-900">Automatic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Color *</Label>
                <Input
                  value={carForm.color}
                  onChange={(e) => setCarForm({...carForm, color: e.target.value})}
                  className="bg-white border-slate-200 text-slate-900"
                  required
                />
              </div>
            </div>

            {/* Image Upload Section */}
            <Accordion type="single" collapsible defaultValue="images" className="w-full">
              <AccordionItem value="images" className="border-slate-200">
                <AccordionTrigger className="text-slate-900 hover:text-blue-900">
                  Photos du véhicule
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-4">
                  <MultiImageUploader
                    value={{
                      main: carForm.image,
                      additional: carForm.images
                        ? carForm.images.split(',').map((s) => s.trim()).filter(Boolean)
                        : [],
                    }}
                    onChange={({ main, additional }) =>
                      setCarForm({
                        ...carForm,
                        image: main,
                        images: (additional || []).join(', '),
                      })
                    }
                  />
                  <p className="text-xs text-slate-500">
                    Optionnel si le statut est "Coming soon" — un placeholder de marque sera affiché.
                    La première photo est la photo de couverture utilisée partout sur le site.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="space-y-2">
              <Label>Status *</Label>
              <Select value={carForm.status} onValueChange={(value) => setCarForm({...carForm, status: value})}>
                <SelectTrigger className="bg-white border-slate-200 text-slate-900">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="available" className="text-slate-900">Available</SelectItem>
                  <SelectItem value="reserved" className="text-slate-900">Reserved</SelectItem>
                  <SelectItem value="sold" className="text-slate-900">Sold</SelectItem>
                  <SelectItem value="coming_soon" className="text-slate-900">Coming soon</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <label
              className="flex items-start gap-3 p-4 border border-slate-200 bg-slate-50 hover:bg-white hover:border-emerald-200 rounded-xl cursor-pointer transition-colors"
              data-testid="car-warranty-toggle-row"
            >
              <input
                type="checkbox"
                checked={carForm.warranty_included !== false}
                onChange={(e) => setCarForm({ ...carForm, warranty_included: e.target.checked })}
                className="w-5 h-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 mt-0.5 cursor-pointer flex-shrink-0"
                data-testid="car-warranty-toggle"
              />
              <div className="flex-1">
                <p className="text-slate-900 font-semibold text-sm">Garantie 1 an incluse</p>
                <p className="text-slate-500 text-xs mt-0.5">
                  Décochez pour les véhicules sans garantie (ex. old-timers). Le badge "Garantie 1 an" ne sera pas affiché sur l'annonce.
                </p>
              </div>
            </label>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={carForm.description}
                onChange={(e) => setCarForm({...carForm, description: e.target.value})}
                className="bg-white border-slate-200 text-slate-900 min-h-24"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Options / Équipements</Label>
              <Textarea
                value={carForm.features}
                onChange={(e) => setCarForm({...carForm, features: e.target.value})}
                className="bg-white border-slate-200 text-slate-900 min-h-32"
                placeholder="Ex : GPS intégré, Cuir, Toit ouvrant, Caméra de recul, Sièges chauffants, Régulateur adaptatif…"
                rows={5}
              />
              <p className="text-xs text-slate-500">Séparez chaque option par une virgule. Le retour à la ligne est autorisé.</p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 btn-primary">
                {editingCar ? 'Update Car' : 'Add Car'}
              </Button>
              <Button 
                type="button" 
                onClick={() => setIsCarDialogOpen(false)} 
                className="flex-1 btn-secondary"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
